/**
 * `/agents/health` — the one proof-based fleet health view (RO-3-13).
 *
 * Rewrite of `excellent/app/agents/health/page.tsx` (464 LOC of server JSX), the
 * largest of the seven `/agents` REWRITEs RO-3-02 deferred here.
 *
 * ## What made this a REWRITE
 *
 * The page called `readSystemStatus()` — which SHELLS OUT to
 * `excellent-mcp system-status --json` — inline in the component body, then
 * rendered everything from the result: a `StatStrip`, three `DataTable`s with
 * their column functions, two side-by-side Cards, and a whole `DodRatchetSection`
 * subtree. Not one line of it lived in a client component, so there was nothing
 * to hand a prop to. `agentHealth.get` (new in RO-3-13) is the endpoint;
 * everything below is the render half, moved verbatim in structure.
 *
 * ## Decision — the failed probe is still a rendered VERDICT, not an error page
 *
 * The endpoint returns `{ok:false, error}` as a 200 payload (see the contract's
 * header for why). This route therefore has THREE states, not two: loading, the
 * oracle-unavailable Card, and the body. Collapsing "the probe failed" into a
 * generic error boundary would delete the single most useful thing this page
 * does — telling an operator that the health probe itself is broken, and how to
 * fix it (`pnpm --filter @excellent/mcp build`).
 *
 * ## Decision — the page NOW OWNS ITS TITLE
 *
 * The original deliberately rendered no `PageHeader`, with a comment explaining
 * that the Agents `ModeShell` already rendered the serif title via
 * `_mode-pulse → SectionTitleBar → Masthead`, so a second header would have its
 * heading suppressed by `HideWhenShellTitled` and leave the description orphaned.
 *
 * That machinery is `excellent/app/`-side chrome and does not exist in this
 * stack — RO-3-00 (#44398) owns porting the shared render layer, and this route
 * renders under the plain `__root` today. So a bare body here would have NO title
 * at all. The header is added back, which restores the oracle provenance the
 * original's comment says "used to" ride in the header and had to be demoted to a
 * footnote. **If RO-3-00's ported shell later supplies the title, this
 * `PageHeader` is the thing to remove** — flagged explicitly because it is a
 * deliberate divergence from the original, not a mechanical carry-over.
 *
 * ## Decision — `settingsHref` goes through the router SEAM, not a typed Link
 *
 * The oracle emits `settingsHref` as an arbitrary runtime string. TanStack's
 * typed `<Link to>` cannot validate one, and the destinations
 * (`/settings/agents/*`) are not ported yet. `RouterLink` (`@excellent/ui/router`,
 * RO-4-01) takes a plain `href` and is what every other ported route in this tree
 * uses, so those links behave exactly as the original's did — and resolve for
 * free once the settings section lands (RO-3-09).
 */

import { createRoute } from "@tanstack/react-router";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  IntentBadge,
  PageHeader,
  StatStrip,
  formatRelativeTime,
  type DataTableColumn,
  type StatStripItem,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import {
  agentHealth,
  type DodRatchetStateDto,
  type DodReceiptDto,
  type SubsystemStatusDto,
  type SystemStatusViewDto,
  type VaultOpsEventDto,
} from "@excellent/ui/api/contracts/agent-health";
import {
  exportAudit,
  type ExportAuditRowDto,
} from "@excellent/ui/api/contracts/export-audit";
import { rootRoute } from "./__root";

const NUM = "tabular-nums";
const MUTED = "type-subtext text-muted-foreground";

function AgentsHealthPage() {
  const { data, error, isLoading } = useApi(agentHealth.get, {});
  // PR-E1 — INV-8 export egress audit (independent of the system-status oracle).
  const {
    data: exportData,
    error: exportError,
    isLoading: exportLoading,
  } = useApi(exportAudit.list, {});

  // Two distinct failures, deliberately distinguished. `error` = the ENDPOINT
  // could not be reached; `!data.ok` = the endpoint answered and told us the
  // ORACLE could not run. The second is the one the original page was built to
  // show, and it carries the oracle's own message.
  const oracleError = error
    ? error instanceof Error
      ? error.message
      : String(error)
    : data && !data.ok
      ? data.error
      : null;

  // Always paint the masthead first (RO-10-08). The oracle can take 10–20s
  // (`excellent-mcp system-status --json`); withholding the h1 until it
  // returns blanks the SPA against the agents-surfaces suite and against the
  // Next server-render, which always shipped the title with the HTML.
  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title="Health"
        description="Every field here is derived mechanically by the system-status oracle — no agent-reported strings."
      />

      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Reading the system-status oracle…</p>
      ) : oracleError !== null || !data?.ok ? (
        <Card>
          <CardHeader>
            <CardTitle>Oracle unavailable</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="type-body text-muted-foreground">
              Could not read the system-status oracle. It runs{" "}
              <code>excellent-mcp system-status --json</code> against the built MCP dist —
              ensure <code>pnpm --filter @excellent/mcp build</code> has run.
            </p>
            <p className={`${MUTED} mt-2 break-words`}>{oracleError}</p>
          </CardContent>
        </Card>
      ) : (
        <HealthBody status={data.status} />
      )}

      <ExportAuditSection
        data={exportData}
        error={exportError}
        isLoading={exportLoading}
      />
    </div>
  );
}

/**
 * PR-E1 surface — what left through the INV-8 door (`export.emitted` audit rows).
 * Independent of the system-status oracle so a slow/failed probe still shows
 * the export census. Never renders artifact bodies — only class/actor/count/hash.
 */
function ExportAuditSection({
  data,
  error,
  isLoading,
}: {
  data: { emittedTotal: number; rows: ExportAuditRowDto[] } | undefined;
  error: unknown;
  isLoading: boolean;
}) {
  const cols: DataTableColumn<ExportAuditRowDto>[] = [
    {
      key: "status",
      header: "Status",
      render: () => (
        <IntentBadge intent="success" withDot>
          emitted
        </IntentBadge>
      ),
    },
    {
      key: "artifactClass",
      header: "Class",
      render: (r) => <span className="text-foreground">{r.artifactClass}</span>,
    },
    {
      key: "itemCount",
      header: "Items",
      render: (r) => <span className={NUM}>{r.itemCount}</span>,
    },
    {
      key: "actorId",
      header: "Actor",
      render: (r) => <span className={MUTED}>{r.actorId}</span>,
    },
    {
      key: "bodyHash",
      header: "Body hash",
      render: (r) => (
        <span className={`${MUTED} font-mono`}>{r.bodyHash.slice(0, 16)}…</span>
      ),
    },
    {
      key: "createdAt",
      header: "When",
      render: (r) => (
        <span className={MUTED}>
          {r.createdAt ? formatRelativeTime(r.createdAt) : "—"}
        </span>
      ),
    },
  ];

  if (isLoading && !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Export egress audit</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="type-body text-muted-foreground">Reading export.emitted rows…</p>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Export egress audit</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="type-body text-muted-foreground">
            Could not read the INV-8 export audit trail (
            <code>export.emitted</code> ops rows).
          </p>
          <p className={`${MUTED} mt-2 break-words`}>
            {error instanceof Error ? error.message : error ? String(error) : "no data"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <DataTable
      title="Export egress audit"
      subtitle={
        data.emittedTotal === 0
          ? "export.emitted = 0 — nothing has crossed the INV-8 door yet"
          : `export.emitted = ${data.emittedTotal} · allowlisted artifacts sealed through exportArtifact`
      }
      rows={data.rows}
      columns={cols}
      rowKey={(r) => String(r.id)}
      empty="No export.emitted audit rows — run excellent-mcp evidence export --task N on a done task."
    />
  );
}

function HealthBody({ status }: { status: SystemStatusViewDto }) {
  const s = status;
  const offDefault = s.subsystems.filter((x) => !x.atDefault);

  const subsystemCols: DataTableColumn<SubsystemStatusDto>[] = [
    {
      key: "label",
      header: "Subsystem",
      render: (r) => <span className="text-foreground">{r.label}</span>,
    },
    {
      key: "state",
      header: "State",
      render: (r) => {
        if (!r.enabled) {
          return (
            <IntentBadge intent="neutral" withDot>
              Off
            </IntentBadge>
          );
        }
        // #45392 - inert (enabled && operating === false) is POSITIVE evidence
        // that the subsystem is switched on but NOT running - never green.
        if (r.inert === true) {
          return (
            <IntentBadge intent="warning" withDot>
              On · not running
            </IntentBadge>
          );
        }
        // operating === null/undefined means not probed (or an older dist with
        // no key) - rendering that green is the fail-open this surface exists
        // to prevent, but it is never inert either (gate-asserted per subsystem
        // in test-system-status.mjs). Only operating === true earns the green On.
        if (r.operating !== true) {
          return (
            <IntentBadge intent="neutral" withDot>
              On · not probed
            </IntentBadge>
          );
        }
        return (
          <IntentBadge intent="success" withDot>
            On
          </IntentBadge>
        );
      },
    },
    {
      key: "default",
      header: "Default",
      render: (r) =>
        r.atDefault ? (
          <span className={MUTED}>{r.defaultOn ? "on" : "off"}</span>
        ) : (
          <IntentBadge intent="warning">
            off default ({r.defaultOn ? "ships on" : "ships off"})
          </IntentBadge>
        ),
    },
    { key: "note", header: "Notes", render: (r) => <span className={MUTED}>{r.note ?? ""}</span> },
    {
      key: "settings",
      header: "",
      render: (r) => (
        // Arbitrary runtime href from the oracle — the router seam, not a typed
        // Link. See this file's header.
        <Link href={r.settingsHref} className="type-subtext text-primary hover:underline">
          Settings ›
        </Link>
      ),
    },
  ];

  // Headline — the mechanical rollup + the numbers that must be zero/nominal.
  // A bare row of <StatCard>s above a framed DataTable reads as floating
  // numbers, so the row takes the shared strip frame (stat-strip.tsx). `board`
  // rather than `standalone` because every cell here carries a status verdict —
  // that variant is the one that renders the leading intent dot.
  const headline: StatStripItem[] = [
    {
      title: "Overall",
      value: s.ok ? "OK" : "Attention",
      intent: s.ok ? "success" : "danger",
      description: s.ok
        ? "converged & clean"
        : s.notOk && s.notOk.length > 0
          ? s.notOk.join(", ")
          : "see failing clauses below",
    },
    {
      title: "Stranded-done",
      value: String(s.strandedDone.count),
      intent: s.strandedDone.count === 0 ? "success" : "danger",
      description: "done but not on main",
    },
    {
      title: "Workers",
      value: `${s.workers.alive} / ${s.workers.dead}`,
      intent: s.workers.dead === 0 ? "neutral" : "danger",
      description: "alive / dead lease",
    },
    {
      title: "Load / core",
      value: `${s.load.perCore.toFixed(2)} / ${s.load.ceilingPerCore.toFixed(1)}`,
      intent: s.load.exceeds ? "warning" : "neutral",
      description: "observed / ceiling",
    },
    {
      // Number-led like its five neighbours: the percentage is the value and the
      // state word is demoted to the support line. A long state ("checkpoint")
      // as the type-heading value overran its cell and collided with the next one.
      title: "Usage window",
      value:
        s.usageWindow.bindingPct != null
          ? `${s.usageWindow.bindingPct}%`
          : s.usageWindow.state,
      intent:
        s.usageWindow.state === "ok" || s.usageWindow.state === "nominal"
          ? "success"
          : "warning",
      description:
        s.usageWindow.bindingPct != null
          ? `of cap · ${s.usageWindow.state}`
          : s.usageWindow.source,
    },
    {
      title: "Braid conflicts",
      value: s.braid.conflicts == null ? "—" : String(s.braid.conflicts),
      intent: !s.braid.conflicts ? "neutral" : "warning",
      description: "open cross-lane",
    },
    {
      // #45025/#45273 — kill-switch liveness. red = verification-loop flags
      // OFF|bypassed while consequence-tagged work is flowing. Guarded on
      // presence: a dist built before it shipped has no loopLiveness key.
      title: "Loop",
      value: s.loopLiveness
        ? s.loopLiveness.red
          ? "OFF"
          : "On"
        : "—",
      intent: s.loopLiveness
        ? s.loopLiveness.red
          ? "danger"
          : "success"
        : "warning",
      description: s.loopLiveness
        ? s.loopLiveness.red
          ? `${s.loopLiveness.reasons.join(", ")} · ${s.loopLiveness.taggedFlowCount} tagged flowing`
          : "verification loop live"
        : "not reported",
    },
    {
      // THE VERIFICATION CANARY — the product's own proof loop, and whether
      // anything is scheduled to run it.
      //
      // WHY THIS TILE EXISTS. The reading behind it (`readCanaryLiveness`) had
      // been correctly reporting this loop dead for 65 hours and had exactly
      // two readers: a hermetic test and a gate. Nothing a person opens
      // consulted it. A computed-and-unconsulted answer is worse than an
      // uncomputed one, because it looks like coverage.
      //
      // THE VALUE IS THE ORACLE'S WORD, NOT A LOCAL MAP. `tile` and `headline`
      // are rendered by the oracle and printed verbatim — the page owns no
      // second copy of the state wording (the #45480 rule). `headline` carries
      // BOTH axes, so a never-armed scheduler and a merely-late loop never
      // render the same line even though the tile word is single-valued.
      //
      // ABSENT IS NOT HEALTHY. A payload with no `canary` key (an older dist)
      // reads "—"/warning/"not reported", never a green tick. A tile that goes
      // green because there is no reading is precisely the silence this exists
      // to end.
      title: "Canary",
      value: s.canary ? s.canary.tile : "—",
      intent: s.canary ? (s.canary.healthy ? "success" : "danger") : "warning",
      description: s.canary ? s.canary.headline : "not reported",
    },
    {
      // "—"/warning for unknown, never "Clear"/success. An unreadable ops
      // ledger tells us nothing about the vault, and rendering nothing-known
      // as green is how a write-blocked shell stays invisible on a health page.
      title: "Vault",
      value:
        s.vault.clear == null
          ? "—"
          : s.vault.clear
            ? "Clear"
            : String(s.vault.openEvents.length),
      intent: s.vault.clear == null ? "warning" : s.vault.clear ? "success" : "danger",
      description:
        s.vault.clear == null
          ? "ops ledger unreadable"
          : s.vault.clear
            ? "no open latch/integrity"
            : "open latch/integrity",
    },
  ];

  const vaultCols: DataTableColumn<VaultOpsEventDto>[] = [
    {
      key: "kind",
      header: "Event",
      render: (r) => <span className="text-foreground">{r.kind}</span>,
    },
    {
      key: "severity",
      header: "Severity",
      render: (r) => (
        <IntentBadge intent={r.severity.toLowerCase() === "critical" ? "danger" : "warning"}>
          {r.severity}
        </IntentBadge>
      ),
    },
    { key: "title", header: "Detail", render: (r) => <span className={MUTED}>{r.title}</span> },
    {
      key: "lastSeenAt",
      header: "Last seen",
      render: (r) => (
        <span className={MUTED}>{r.lastSeenAt ? formatRelativeTime(r.lastSeenAt) : "—"}</span>
      ),
    },
  ];

  return (
    <>
      <StatStrip items={headline} variant="board" />

      {/* #44812/#45273 — the health clauses that are actually failing, named.
          A board that says only "Attention" is not invertible: the operator
          cannot tell WHICH of the six clauses fired without reading every
          block. Render each clause name (and its Oracle-attributed detail
          line) so a red health page says what it is red FOR. */}
      {(!s.ok || (s.notOk && s.notOk.length > 0)) && (
        <Card>
          <CardHeader>
            <CardTitle>Failing clauses</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {s.notOk && s.notOk.length > 0 ? (
              s.notOk.map((name, i) => {
                // #45480 — render the oracle-attributed line (with live counts),
                // NOT a hand-maintained second copy of HEALTH_CLAUSES. The badge
                // owns the clause name, so the leading "name: " is dropped from
                // the carried detail; an older dist with no notOkDetail falls
                // back to the bare name.
                const detail = s.notOkDetail?.[i];
                return (
                  <div key={name} className="flex items-center gap-2 type-body">
                    <IntentBadge intent="danger" withDot>
                      {name}
                    </IntentBadge>
                    <span className={MUTED}>
                      {detail ? detail.replace(`${name}: `, "") : name}
                    </span>
                  </div>
                );
              })
            ) : (
              <p className="type-body text-muted-foreground">
                Health reports red here, but did not name a failing clause — the
                oracle predates clause attribution (#44812).
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subsystems & defaults — the automation-plane switches, one place. */}
      <DataTable
        title="Subsystems & defaults"
        subtitle={
          offDefault.length === 0
            ? "every subsystem is at its shipped default"
            : `${offDefault.length} toggled away from the shipped default (${offDefault
                .map((x) => x.label)
                .join(", ")})`
        }
        rows={s.subsystems}
        columns={subsystemCols}
        rowKey={(r) => r.key}
        empty="No subsystems reported."
      />
      <p className={MUTED}>
        Reflects the saved workspace settings, read through each subsystem&rsquo;s canonical
        enabled-reader. An environment-variable override (e.g. <code>EXCELLENT_BRAID=0</code>)
        wins at runtime and is not shown here.
      </p>

      {/*
        Vault — the 07-25 latched-shell outage class, which was invisible here
        until #44113. Sourced from the DURABLE ops rows, not the in-memory write
        latch: the latch lives in the process that tripped it, so this page's
        freshly-spawned oracle would always read "clear". A latch that has since
        auto-healed resolves its ops row, so healing shows up as this table
        emptying — that is the "vault heal" surface.
      */}
      <DataTable
        title="Vault — write latch & integrity"
        subtitle={
          s.vault.clear == null
            ? "the ops ledger could not be read — treat this as UNKNOWN, not clear"
            : s.vault.clear
              ? "no open vault events; a latch that healed resolves its row and leaves this empty"
              : `${s.vault.openEvents.length} open — writes may be blocked on the node that tripped it`
        }
        rows={s.vault.openEvents}
        columns={vaultCols}
        rowKey={(r) => `${r.kind}-${r.firstSeenAt ?? ""}`}
        empty={s.vault.clear == null ? "Ops ledger unreadable." : "No open vault events."}
      />

      {/*
        Definition of Done — the ratchet's own telemetry (#44086, RATCHET-
        UNIFICATION-PRD §3). Until now dod_criteria / gate_run_receipts / the
        dod.* ops rows were readable only by CLI or SQL: telemetry that lives
        where a user cannot reach it is not a product. Rendered only when the
        oracle carries the block — an older built dist emits no `dod` key.
      */}
      {s.dod ? <DodRatchetSection dod={s.dod} /> : null}

      {/* Gate contract + Git/mirror, side by side on wide screens. */}
      <div className="grid grid-cols-1 gap-4 @4xl/page:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Gate contract</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className={`type-heading ${NUM} text-foreground`}>
                {s.gates.suiteCount ?? "—"}
              </span>
              <span className={MUTED}>gates in the verify:checks suite</span>
            </div>
            <div>
              <p className="type-subtext font-medium text-muted-foreground">
                Recursive-Done (Definition-of-Done) gates
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {s.gates.recursiveDone.map((g) => (
                  <Badge key={g} variant="secondary">
                    {g}
                  </Badge>
                ))}
              </div>
            </div>
            <p className={MUTED}>{s.gates.runsVia}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Git &amp; mirror</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 type-body">
            <Row label="main" value={s.git.main ? s.git.main.slice(0, 12) : "—"} />
            <Row
              label={`tier (${s.git.tier.ref})`}
              value={
                s.git.tier.sha == null
                  ? "none"
                  : s.git.tier.mainIsAncestor
                    ? `converged · +${s.git.tier.aheadOfMain ?? 0}`
                    : "diverged from main"
              }
              intent={
                s.git.tier.sha == null || s.git.tier.mainIsAncestor ? "neutral" : "danger"
              }
            />
            <Row
              label="mirror (origin/main)"
              value={
                s.git.mirror.ffable == null
                  ? "not configured"
                  : s.git.mirror.ffable
                    ? `ff-able · main +${s.git.mirror.mainAheadOfOrigin ?? 0}`
                    : "NOT ff-able"
              }
              intent={s.git.mirror.ffable === false ? "danger" : "neutral"}
            />
            <Row
              label="tick singleton"
              value={
                !s.tickSingleton.held
                  ? "free"
                  : s.tickSingleton.holderAlive
                    ? `held · pid ${s.tickSingleton.holderPid} (alive)`
                    : `held · pid ${s.tickSingleton.holderPid} (DEAD)`
              }
              intent={
                s.tickSingleton.held && !s.tickSingleton.holderAlive ? "warning" : "neutral"
              }
            />
            <Row
              label="goals"
              value={`${s.goals.active} active · ${s.goals.done} done · ${s.goals.abandoned} abandoned`}
            />
          </CardContent>
        </Card>
      </div>

      <p className={MUTED}>
        Every field above is derived mechanically by the system-status oracle — no
        agent-reported strings. node <span className="text-foreground">{s.node}</span> · schema v
        {s.schemaVersion} · generated {formatRelativeTime(s.generatedAt)}
      </p>
    </>
  );
}

function DodRatchetSection({ dod }: { dod: DodRatchetStateDto }) {
  if (!dod.readable) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Definition of Done — ratchet</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="type-body text-muted-foreground">
            The criteria and receipt tables could not be read. Treat this as UNKNOWN, not as
            &ldquo;nothing is bound&rdquo; — an unreadable ledger is not a clean board.
          </p>
        </CardContent>
      </Card>
    );
  }

  const receiptCols: DataTableColumn<DodReceiptDto>[] = [
    {
      key: "taskId",
      header: "Task",
      render: (r) => (
        // `/work/issues` is RO-3-03's; the router seam carries the href so this
        // resolves the moment that section lands, exactly as the original did.
        <Link
          href={`/work/issues?peek=${r.taskId}`}
          className="type-subtext text-primary hover:underline"
        >
          #{r.taskId}
        </Link>
      ),
    },
    {
      key: "command",
      header: "Gate",
      render: (r) => <span className="text-foreground">{r.command}</span>,
    },
    {
      key: "exitCode",
      header: "Result",
      render: (r) =>
        r.exitCode === 0 ? (
          <IntentBadge intent="success" withDot>
            passed
          </IntentBadge>
        ) : (
          <IntentBadge intent="danger" withDot>
            exit {r.exitCode}
          </IntentBadge>
        ),
    },
    {
      key: "mode",
      header: "Mode",
      render: (r) => <span className={MUTED}>{r.mode ?? "absolute"}</span>,
    },
    {
      key: "binding",
      header: "Binding",
      render: (r) =>
        r.binding === "staged" ? (
          <span className={MUTED}>staged</span>
        ) : (
          <IntentBadge intent="warning">{r.binding}</IntentBadge>
        ),
    },
    {
      key: "finishedAt",
      header: "Ran",
      render: (r) => (
        <span className={MUTED}>{r.finishedAt ? formatRelativeTime(r.finishedAt) : "—"}</span>
      ),
    },
  ];

  return (
    <>
      <div className="grid grid-cols-1 gap-4 @4xl/page:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Bound criteria</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 type-body">
            <div className="flex items-baseline gap-2">
              <span className={`type-heading ${NUM} text-foreground`}>{dod.criteria.bound}</span>
              <span className={MUTED}>criteria currently gating `done`</span>
            </div>
            <Row label="across tasks" value={String(dod.criteria.tasks)} />
            <Row
              label="mechanically provable (gate: refs)"
              value={`${dod.criteria.gateRefs} of ${dod.criteria.bound}`}
            />
            <Row
              label="open dod.* ops alarms"
              value={dod.openOps == null ? "—" : String(dod.openOps)}
              intent={dod.openOps ? "warning" : "neutral"}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Gate receipts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 type-body">
            <div className="flex items-baseline gap-2">
              <span className={`type-heading ${NUM} text-foreground`}>
                {dod.receipts.passing}/{dod.receipts.total}
              </span>
              <span className={MUTED}>passing</span>
            </div>
            <Row
              label="failing"
              value={String(dod.receipts.failing)}
              intent={dod.receipts.failing ? "warning" : "neutral"}
            />
            <Row label="delta-attested" value={String(dod.receipts.delta)} />
            <Row
              label="not staged-bound"
              value={String(dod.receipts.unbound)}
              intent={dod.receipts.unbound ? "danger" : "neutral"}
            />
          </CardContent>
        </Card>
      </div>

      <DataTable
        title="Recent gate receipts"
        subtitle="every receipt is the recorded execution of a gate against a tree hash — the proof a criterion was met, not an agent's claim that it was"
        rows={dod.recent}
        columns={receiptCols}
        rowKey={(r) => `${r.taskId}-${r.command}-${r.finishedAt ?? ""}`}
        empty="No gate receipts recorded yet."
      />
    </>
  );
}

function Row({
  label,
  value,
  intent,
}: {
  label: string;
  value: string;
  intent?: "neutral" | "danger" | "warning";
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="type-subtext text-muted-foreground">{label}</span>
      {intent && intent !== "neutral" ? (
        <IntentBadge intent={intent}>{value}</IntentBadge>
      ) : (
        <span className={`${NUM} text-foreground`}>{value}</span>
      )}
    </div>
  );
}

export const agentsHealthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/health",
  component: AgentsHealthPage,
});
